import type { CatalogResponse, CheckoutResponse, ClaimResponse, Cosmetics, DevLabRequest, GemPack, InboxResponse, ItemType, Loadout, MeResponse, RewardBundle, StoreOffer, VerifyPurchaseResponse } from '@vm/shared';
import { audio } from '../../game/audio/Audio';
import { errorCode, errorMessage } from '../api/http';
import { api } from '../api/routes';
import { recoverPurchases as recoverStorePurchases, settle, startPurchase, watchPurchases } from '../billing';
import { isNativeApp, openExternal, platform, portal, storeName } from '../platform';
import { rewardSummary } from './content';
import { track } from './telemetry';
import { cacheCatalog, cacheMe } from './offline';
import { achievements, battlePass, catalog, cosmetics, inbox, isOffline, loadout, me, patchMe, quests, setWallet, toast } from './store';

// Helpers ---------------------------------------------------------------------------------------------

export function requireOnline(what = 'That'): boolean {
  if (!isOffline.value) return true;
  toast("You're offline", { kind: 'warning', body: `${what} needs a connection to the Voxel Marines servers.` });
  return false;
}

/** Runs an API call, toasting a friendly error on failure. Resolves undefined on failure. */
export async function attempt<T>(fn: () => Promise<T>, opts: { success?: string; errorTitle?: string } = {}): Promise<T | undefined> {
  try {
    const r = await fn();
    if (opts.success) toast(opts.success, { kind: 'success' });
    return r;
  } catch (e) {
    if (opts.errorTitle) toast(opts.errorTitle, { kind: 'error', body: errorMessage(e) });
    else toast(errorMessage(e), { kind: 'error' });
    return undefined;
  }
}

export function setMe(m: MeResponse): void {
  me.value = m;
  cacheMe(m);
}

function saveLocal(): void {
  if (me.value) cacheMe(me.value);
}

export function rewardToast(title: string, r: RewardBundle | null | undefined): void {
  const parts = rewardSummary(r);
  toast(title, { kind: 'reward', body: parts.length ? `+ ${parts.join('  ·  ')}` : undefined, ttl: 4500 });
}

/** Applies a reward claim (wallet, progression and newly granted inventory items). */
function applyClaim(res: ClaimResponse): void {
  const cur = me.value;
  if (!cur) return;
  const have = new Set(cur.inventory.map((i) => `${i.type}:${i.id}`));
  const added = (res.items ?? []).filter((i) => !have.has(`${i.type}:${i.id}`));
  me.value = { ...cur, wallet: res.wallet ?? cur.wallet, progression: res.progression ?? cur.progression, inventory: added.length ? [...cur.inventory, ...added] : cur.inventory };
  saveLocal();
}

// Loading ------------------------------------------------------------------------------------------------

export async function loadMe(): Promise<MeResponse> {
  const m = await api.me.get();
  setMe(m);
  return m;
}

export async function loadCatalog(): Promise<CatalogResponse> {
  const c = await api.catalog();
  catalog.value = c;
  cacheCatalog(c);
  return c;
}

export async function refreshMe(): Promise<void> {
  if (isOffline.value) return;
  try {
    await loadMe();
  } catch {
    /* keep the current profile; the next action will surface errors */
  }
}

export async function loadQuests(): Promise<void> {
  if (isOffline.value) return;
  quests.value = await api.quests.get();
}

export async function loadBattlePass(): Promise<void> {
  if (isOffline.value) return;
  battlePass.value = await api.battlepass.get();
}

export async function loadAchievements(): Promise<void> {
  if (isOffline.value) return;
  achievements.value = (await api.achievements.get()).achievements;
}

function applyInbox(res: InboxResponse): void {
  inbox.value = [...res.messages].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  if (me.value) me.value = { ...me.value, unreadInbox: res.unread };
}

export async function loadInbox(): Promise<void> {
  if (isOffline.value) return;
  applyInbox(await api.inbox.get());
}

/** Loads the lobby's secondary data in the background (errors are ignored until opened). */
export function loadLobbyData(): void {
  if (isOffline.value) return;
  void loadQuests().catch(() => {});
  void loadBattlePass().catch(() => {});
  void loadAchievements().catch(() => {});
}

// Shop ---------------------------------------------------------------------------------------------------

export async function buyItem(type: ItemType, id: string, label: string): Promise<boolean> {
  if (!requireOnline('Buying')) return false;
  const res = await attempt(() => api.shop.buy({ type, id }), { errorTitle: `Couldn't buy ${label}` });
  if (res === undefined) return false;
  patchMe(res);
  saveLocal();
  audio.play('ui_buy');
  toast(`${label} unlocked!`, { kind: 'success' });
  return true;
}

export async function buyOffer(offer: StoreOffer): Promise<boolean> {
  if (!requireOnline('Buying')) return false;
  const res = await attempt(() => api.shop.buy({ type: 'offer', id: offer.id }), { errorTitle: `Couldn't buy ${offer.name}` });
  if (res === undefined) return false;
  patchMe(res);
  saveLocal();
  audio.play('ui_buy');
  toast(`${offer.name} is yours!`, { kind: 'success' });
  return true;
}

/** Starts a gem pack purchase on the right payment rail for this platform. */
export async function buyGemPack(pack: GemPack): Promise<void> {
  if (portal) return; // gems are not sold inside a web portal
  if (!requireOnline('Purchases')) return;
  const step = (label: string): void => track('iap', { label, pack: pack.id });
  step('click');
  if (me.value?.restricted && !isNativeApp) {
    step('blocked_restricted');
    toast('Ask a parent', { kind: 'warning', body: 'Gem purchases on your account can only be made by a parent through the app store.' });
    return;
  }
  // Desktop checkout runs in the system browser, which can't return to app://; the API falls back to the web app.
  const returnUrl = platform === 'web' ? `${location.origin}${location.pathname}?purchase=done` : undefined;
  let res: CheckoutResponse;
  try {
    res = await api.iap.checkout({ packId: pack.id, platform, returnUrl });
  } catch (e) {
    step('checkout_error');
    toast(errorCode(e) === 'restricted' ? 'Ask a parent' : "Couldn't start checkout", { kind: 'error', body: errorMessage(e) });
    return;
  }
  if (res.provider === 'dev') {
    step('completed');
    setWallet(res.wallet);
    saveLocal();
    audio.play('ui_buy');
    toast(`+${(pack.gems + pack.bonus).toLocaleString()} gems`, { kind: 'reward', body: 'Test purchase granted instantly (dev payments).' });
    return;
  }
  if (res.provider === 'stripe') {
    step('checkout_opened');
    toast('Opening secure checkout…', { kind: 'info' });
    openExternal(res.url, true);
    return;
  }
  const userId = me.value?.id;
  if (!userId) return;
  const started = await startPurchase(res.sku, userId);
  step(started.kind === 'purchased' ? 'store_paid' : started.kind);
  if (started.kind === 'cancelled') return;
  if (started.kind === 'failed') {
    toast("Purchase didn't go through", { kind: 'warning', body: started.message });
    return;
  }
  if (started.kind === 'deferred') {
    toast('Purchase pending', { kind: 'info', body: `Your gems will arrive as soon as ${storeName()} completes the payment.` });
    return;
  }
  const out = await settle(started.receipt);
  if (out.kind === 'retry') {
    toast('Purchase received', { kind: 'info', body: `We couldn't reach our servers to add your gems. They will be added automatically, so you won't be charged twice.` });
    return;
  }
  if (out.kind === 'rejected') {
    toast("We couldn't verify that purchase", { kind: 'error', body: out.message });
    return;
  }
  setWallet(out.res.wallet);
  saveLocal();
  if (out.kind === 'pending') {
    toast('Purchase pending', { kind: 'info', body: 'Your gems will arrive as soon as the store confirms it.' });
    return;
  }
  step('completed');
  audio.play('ui_buy');
  toast(`+${out.res.gems.toLocaleString()} gems`, { kind: 'reward', body: 'Thanks for supporting Voxel Marines!' });
}

function purchaseDelivered(res: VerifyPurchaseResponse): void {
  setWallet(res.wallet);
  saveLocal();
  audio.play('ui_buy');
  toast('Gems delivered', { kind: 'reward', body: `Your ${res.gems.toLocaleString()}-gem purchase has been added to your wallet.` });
}

/** Delivers store purchases that were paid for but not yet credited (native apps only). */
export async function recoverPurchases(): Promise<void> {
  const userId = me.value?.id;
  if (!isNativeApp || !userId || isOffline.value) return;
  await recoverStorePurchases(userId, purchaseDelivered);
}

/** Starts store-purchase delivery for this session: recovery now, plus late iOS approvals. */
export function startPurchaseDelivery(): void {
  if (!isNativeApp) return;
  void watchPurchases(() => me.value?.id ?? null, purchaseDelivered).catch(() => {});
  void recoverPurchases().catch(() => {});
}

// Loadout & cosmetics --------------------------------------------------------------------------------------

export async function saveLoadout(next: Loadout): Promise<boolean> {
  const cur = me.value;
  if (!cur) return false;
  const prev = cur.loadout;
  me.value = { ...cur, loadout: next };
  if (isOffline.value) {
    saveLocal();
    return true;
  }
  try {
    setMe(await api.me.setLoadout(next));
    return true;
  } catch (e) {
    if (me.value) me.value = { ...me.value, loadout: prev };
    toast("Couldn't change your loadout", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

export function equipWeapon(slot: number, weaponId: string): Promise<boolean> {
  const l = loadout.value;
  const weapons = [...l.weapons];
  while (weapons.length < 5) weapons.push('');
  weapons[slot] = weaponId;
  audio.play('ui_click');
  return saveLoadout({ ...l, weapons });
}

export function equipGadget(gadgetId: string): Promise<boolean> {
  audio.play('ui_click');
  return saveLoadout({ ...loadout.value, gadget: gadgetId });
}

/** Equips a companion pet ('' = no pet). */
export function equipPet(petId: string): Promise<boolean> {
  audio.play('ui_click');
  return saveLoadout({ ...loadout.value, pet: petId });
}

export async function saveCosmetics(next: Cosmetics): Promise<boolean> {
  const cur = me.value;
  if (!cur) return false;
  const prev = cur.cosmetics;
  me.value = { ...cur, cosmetics: next };
  if (isOffline.value) {
    saveLocal();
    return true;
  }
  try {
    setMe(await api.me.setCosmetics(next));
    return true;
  } catch (e) {
    if (me.value) me.value = { ...me.value, cosmetics: prev };
    toast("Couldn't change your look", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

export function equipCosmetic(patch: Partial<Cosmetics>): Promise<boolean> {
  audio.play('ui_click');
  return saveCosmetics({ ...cosmetics.value, ...patch });
}

/** Wears a pet style on the equipped pet ('' = the pet's own colors). */
export function equipPetStyle(styleId: string): Promise<boolean> {
  return equipCosmetic({ petStyle: styleId });
}

export function setCamo(weaponId: string, camoId: string): Promise<boolean> {
  const camos = { ...(cosmetics.value.camos ?? {}) };
  if (camoId === 'none') delete camos[weaponId];
  else camos[weaponId] = camoId;
  return equipCosmetic({ camos });
}

/** Picks the bullet tracer style for one (hitscan) weapon; 'none' goes back to the weapon's own tracer. */
export function setTracer(weaponId: string, tracerId: string): Promise<boolean> {
  const tracers = { ...(cosmetics.value.tracers ?? {}) };
  if (tracerId === 'none') delete tracers[weaponId];
  else tracers[weaponId] = tracerId;
  return equipCosmetic({ tracers });
}

/** Fits a sight on one weapon; 'none' goes back to the weapon's own sights. The server re-validates ownership and class fit. */
export function setScope(weaponId: string, scopeId: string): Promise<boolean> {
  const scopes = { ...(cosmetics.value.scopes ?? {}) };
  if (scopeId === 'none') delete scopes[weaponId];
  else scopes[weaponId] = scopeId;
  return equipCosmetic({ scopes });
}

/** Test Lab (development builds only): whether this API offers it. Cached after the first answer. */
let devLabAvailable: Promise<boolean> | null = null;
export function devLabEnabled(): Promise<boolean> {
  devLabAvailable ??= api.dev.status().then((r) => r.enabled, () => false);
  return devLabAvailable;
}

/** Test Lab shortcut: unlock everything, set level, add currency or mastery kills. The server refuses outside development builds. */
export async function runTestLab(req: DevLabRequest, label: string): Promise<boolean> {
  if (!requireOnline('The Test Lab')) return false;
  const res = await attempt(() => api.dev.lab(req), { errorTitle: `Test Lab: ${label} failed` });
  if (res === undefined) return false;
  patchMe(res);
  saveLocal();
  audio.play('ui_buy');
  toast(label, { kind: 'success' });
  return true;
}

export async function saveCustomSkin(slot: number, name: string, data: string): Promise<boolean> {
  if (!requireOnline('Saving skins')) return false;
  const prevData = me.value?.customSkins.find((s) => s.slot === slot)?.data;
  const res = await attempt(() => api.me.saveSkin(slot, { name, data }), { errorTitle: "Couldn't save your skin" });
  if (!res) return false;
  setMe(res);
  // Keep wearing the skin if the edited slot was equipped.
  if (prevData && prevData !== data && cosmetics.value.skin === prevData) await equipCosmetic({ skin: data });
  toast(`Saved "${name}"`, { kind: 'success' });
  return true;
}

export async function deleteCustomSkin(slot: number): Promise<boolean> {
  if (!requireOnline('Deleting skins')) return false;
  const skin = me.value?.customSkins.find((s) => s.slot === slot);
  const res = await attempt(() => api.me.deleteSkin(slot), { errorTitle: "Couldn't delete that skin" });
  if (!res) return false;
  setMe(res);
  if (skin && cosmetics.value.skin === skin.data) await equipCosmetic({ skin: 'preset:marine' });
  return true;
}

// Profile ----------------------------------------------------------------------------------------------------

export async function rename(name: string): Promise<boolean> {
  if (!requireOnline('Changing your name')) return false;
  try {
    setMe(await api.me.update({ name }));
    return true;
  } catch (e) {
    toast("Couldn't change your name", { kind: 'error', body: errorMessage(e) });
    return false;
  }
}

/** Free generated call signs from the server (the only names restricted accounts may pick). */
export async function nameSuggestions(): Promise<string[]> {
  if (isOffline.value) return [];
  try {
    return (await api.me.nameSuggestions()).names;
  } catch {
    return [];
  }
}

export async function markTutorialDone(): Promise<void> {
  if (me.value) me.value = { ...me.value, tutorialDone: true };
  saveLocal();
  if (isOffline.value) return;
  try {
    setMe(await api.me.update({ tutorialDone: true }));
  } catch {
    /* non-critical */
  }
}

/** Boot Camp finished: claims its one-time reward (offline or already claimed: just the cheer). */
export async function claimBootCamp(): Promise<void> {
  let reward: RewardBundle | null = null;
  if (!isOffline.value) {
    try {
      const res = await api.rewards.bootcamp();
      setWallet(res.wallet);
      reward = res.reward;
    } catch {
      /* already claimed, or the API is unreachable */
    }
  }
  const parts = rewardSummary(reward);
  toast('Boot Camp complete!', {
    kind: 'reward',
    body: parts.length ? `+ ${parts.join('  ·  ')}: grab your first new weapon in the Armory!` : "You're ready for the real thing. Hit PLAY when you are.",
    ttl: 6000,
  });
  audio.play('levelup');
}

// Daily login ------------------------------------------------------------------------------------------------

export async function claimDaily(): Promise<RewardBundle | null> {
  if (!requireOnline('Daily rewards')) return null;
  const res = await attempt(() => api.rewards.daily(), { errorTitle: "Couldn't claim today's reward" });
  if (!res) return null;
  setWallet(res.wallet);
  if (me.value) me.value = { ...me.value, dailyLogin: { ...me.value.dailyLogin, day: res.day, claimedToday: true } };
  audio.play('ui_buy');
  void refreshMe();
  return res.reward;
}

// Quests / battle pass / achievements -----------------------------------------------------------------------

export async function claimQuest(id: string): Promise<void> {
  if (!requireOnline('Claiming')) return;
  const q = [...(quests.value?.daily ?? []), ...(quests.value?.weekly ?? [])].find((x) => x.id === id);
  const res = await attempt(() => api.quests.claim(id), { errorTitle: "Couldn't claim that quest" });
  if (!res) return;
  applyClaim(res);
  quests.value = res.quests;
  audio.play('ui_buy');
  rewardToast(q ? `Quest complete: ${q.title}` : 'Quest reward claimed', res.reward);
  void loadBattlePass().catch(() => {});
}

export async function rerollQuest(id: string): Promise<void> {
  if (!requireOnline('Rerolling')) return;
  const res = await attempt(() => api.quests.reroll(id), { errorTitle: "Couldn't reroll that quest" });
  if (!res) return;
  quests.value = res;
  audio.play('ui_click');
  toast('New quest assigned', { kind: 'info' });
}

export async function claimTier(tier: number, track: 'free' | 'premium'): Promise<void> {
  if (!requireOnline('Claiming')) return;
  const res = await attempt(() => api.battlepass.claim({ tier, track }), { errorTitle: "Couldn't claim that reward" });
  if (!res) return;
  applyClaim(res);
  battlePass.value = res.state;
  audio.play('ui_buy');
  rewardToast(`Tier ${tier} reward claimed`, res.reward);
}

export async function claimAllTiers(): Promise<void> {
  if (!requireOnline('Claiming')) return;
  const res = await attempt(() => api.battlepass.claimAll(), { errorTitle: "Couldn't claim your rewards" });
  if (!res) return;
  applyClaim(res);
  battlePass.value = res.state;
  audio.play('ui_buy');
  // Many items: count them instead of naming every one.
  const items = res.reward.items ?? [];
  const parts = rewardSummary(items.length > 3 ? { ...res.reward, items: undefined } : res.reward);
  if (items.length > 3) parts.push(`${items.length} items`);
  toast(res.count === 1 ? 'Reward claimed' : `${res.count} rewards claimed`, { kind: 'reward', body: parts.length ? `+ ${parts.join('  ·  ')}` : undefined, ttl: 4500 });
}

export async function buyPremiumPass(): Promise<boolean> {
  if (!requireOnline('The premium pass')) return false;
  const res = await attempt(() => api.battlepass.buyPremium(), { errorTitle: "Couldn't unlock the premium pass" });
  if (!res) return false;
  battlePass.value = res.state;
  setWallet(res.wallet);
  saveLocal();
  audio.play('levelup');
  toast('Premium pass unlocked!', { kind: 'reward', body: 'Every premium reward you have reached is ready to claim.' });
  return true;
}

export async function claimAchievement(id: string): Promise<void> {
  if (!requireOnline('Claiming')) return;
  const a = achievements.value?.find((x) => x.id === id);
  const res = await attempt(() => api.achievements.claim(id), { errorTitle: "Couldn't claim that achievement" });
  if (!res) return;
  applyClaim(res);
  if (achievements.value) achievements.value = achievements.value.map((x) => (x.id === id ? res.achievement : x));
  audio.play('ui_buy');
  rewardToast(`Achievement: ${a?.title ?? res.achievement.title}`, res.reward);
}

// Inbox -------------------------------------------------------------------------------------------------------

export async function readInboxMessage(id: string): Promise<void> {
  const list = inbox.value;
  const msg = list?.find((m) => m.id === id);
  if (!list || !msg || msg.read || isOffline.value) return;
  inbox.value = list.map((m) => (m.id === id ? { ...m, read: true } : m));
  if (me.value) me.value = { ...me.value, unreadInbox: Math.max(0, me.value.unreadInbox - 1) };
  try {
    applyInbox(await api.inbox.read(id));
  } catch {
    /* read receipts are best-effort */
  }
}

export async function claimInboxMessage(id: string): Promise<void> {
  if (!requireOnline('Claiming')) return;
  const res = await attempt(() => api.inbox.claim(id), { errorTitle: "Couldn't claim those rewards" });
  if (!res) return;
  applyClaim(res);
  if (inbox.value) inbox.value = inbox.value.map((m) => (m.id === id ? res.message : m));
  audio.play('ui_buy');
  rewardToast('Rewards claimed', res.reward);
}

export async function deleteInboxMessage(id: string): Promise<void> {
  if (!requireOnline('Deleting')) return;
  const res = await attempt(() => api.inbox.remove(id), { errorTitle: "Couldn't delete that message" });
  if (!res) return;
  applyInbox(res);
}
